// ============================================================================
// OPTIMIZED POLYGON LOCATIONS SERVICE - PERFORMANCE ENHANCED
// frontend/src/services/polygonLocations.service.ts
// ============================================================================

import { initializeApp, FirebaseApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  onSnapshot, 
  doc, 
  getDoc,
  getDocs,
  query,
  orderBy,
  Firestore,
  QuerySnapshot,
  DocumentData,
  DocumentSnapshot,
  Unsubscribe
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey: "AIzaSyC_cwJ2lBqsDMzWmGIlcBqyBb2BqP3I6aA",
  authDomain: "face-authentication-app-44ddb.firebaseapp.com",
  projectId: "face-authentication-app-44ddb",
  storageBucket: "face-authentication-app-44ddb.firebasestorage.app",
  messagingSenderId: "541867932129",
  appId: "1:541867932129:android:ed73e3859b053c1555faa0"
};

export interface FirebaseCoordinate {
  latitude: number;
  longitude: number;
}

export interface FirebasePolygonLocation {
  centerLatitude: number;
  centerLongitude: number;
  coordinates: FirebaseCoordinate[];
  description: string;
  isActive: boolean;
  name: string;
  updatedAt: string;
}

export interface ProjectLocation {
  id: string;
  name: string;
  description: string;
  coordinates: { lat: number; lng: number };
  polygon: google.maps.LatLngLiteral[];
  status: 'active' | 'inactive';
  type: 'construction' | 'renovation' | 'maintenance';
  updatedAt: string;
  isActive: boolean;
  createdAt?: string;
  projectManager?: string;
  estimatedCompletion?: string;
  budget?: number;
  area?: number;
  color?: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
}

export interface ProjectLocationStats {
  total: number;
  active: number;
  inactive: number;
  byType: Record<string, number>;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  totalArea: number;
  averagePolygonPoints: number;
  lastUpdated: string;
}

export interface ProjectLocationFilter {
  status?: 'active' | 'inactive';
  type?: 'construction' | 'renovation' | 'maintenance';
  priority?: 'low' | 'medium' | 'high' | 'critical';
  hasEmployees?: boolean;
  minArea?: number;
  maxArea?: number;
  updatedAfter?: Date;
}

export type ProjectLocationCallback = (locations: ProjectLocation[]) => void;
export type UnsubscribeFunction = Unsubscribe;

// ============================================================================
// STATIC CONFIGURATION
// ============================================================================

const PROJECT_COLORS = [
  '#38b2a8', '#f59e0b', '#8b5cf6', '#ef4444', '#10b981',
  '#3b82f6', '#f97316', '#84cc16', '#06b6d4', '#ec4899',
  '#6366f1', '#14b8a6', '#f43f5e', '#a855f7', '#22c55e',
  '#0ea5e9', '#fb7185', '#34d399', '#fbbf24', '#c084fc',
];

// ============================================================================
// SERVICE CLASS
// ============================================================================

class PolygonLocationsService {
  private db: Firestore;
  private app: FirebaseApp;
  private listeners: Map<string, UnsubscribeFunction> = new Map();
  private cache: Map<string, ProjectLocation> = new Map();
  private lastFetchTime: number = 0;
  private cacheExpiryTime: number = 5 * 60 * 1000; // 5 minutes
  private _allLocationsCache: ProjectLocation[] | null = null;

  constructor() {
    this.app = initializeApp(firebaseConfig, 'polygonLocationsFirestore');
    this.db = getFirestore(this.app);
  }

  /**
   * Generate a unique color for each project
   */
  private generateProjectColor(projectId: string): string {
    let hash = 0;
    for (let i = 0; i < projectId.length; i++) {
      const char = projectId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return PROJECT_COLORS[Math.abs(hash) % PROJECT_COLORS.length];
  }

  /**
   * Calculate polygon area in square meters
   */
  private calculatePolygonArea(polygon: google.maps.LatLngLiteral[]): number {
    if (polygon.length < 3) return 0;
    
    let area = 0;
    const n = polygon.length;
    
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += polygon[i].lat * polygon[j].lng;
      area -= polygon[j].lat * polygon[i].lng;
    }
    
    area = Math.abs(area) / 2;
    const metersPerDegree = 111320;
    return area * metersPerDegree * metersPerDegree;
  }

  /**
   * Determine project priority
   */
  private determineProjectPriority(
    isActive: boolean, 
    type: string, 
    area: number
  ): 'low' | 'medium' | 'high' | 'critical' {
    if (!isActive) return 'low';
    
    if (type === 'construction') {
      if (area > 10000) return 'critical';
      if (area > 5000) return 'high';
      return 'medium';
    } else if (type === 'renovation') {
      if (area > 5000) return 'high';
      return 'medium';
    }
    return 'low';
  }

  /**
   * Convert Firestore data to ProjectLocation format
   */
  private convertFirestoreToProjectLocation(
    id: string, 
    firestoreData: DocumentData
  ): ProjectLocation {
    const polygon: google.maps.LatLngLiteral[] = firestoreData.coordinates?.map((coord: FirebaseCoordinate) => ({
      lat: coord.latitude,
      lng: coord.longitude
    })) || [];

    const area = this.calculatePolygonArea(polygon);

    const getProjectType = (description: string): 'construction' | 'renovation' | 'maintenance' => {
      const desc = description?.toLowerCase() || '';
      if (desc.includes('renovation') || desc.includes('refurbishment') || desc.includes('remodel')) {
        return 'renovation';
      } else if (desc.includes('maintenance') || desc.includes('repair') || desc.includes('fix')) {
        return 'maintenance';
      }
      return 'construction';
    };

    const projectType = getProjectType(firestoreData.description || '');
    const isActive = firestoreData.isActive || false;
    const priority = this.determineProjectPriority(isActive, projectType, area);

    return {
      id,
      name: firestoreData.name || 'Unknown Project',
      description: firestoreData.description || 'No description available',
      coordinates: {
        lat: firestoreData.centerLatitude || 25.2048,
        lng: firestoreData.centerLongitude || 55.2708
      },
      polygon,
      status: isActive ? 'active' : 'inactive',
      type: projectType,
      updatedAt: firestoreData.updatedAt || new Date().toISOString(),
      isActive,
      createdAt: firestoreData.createdAt || firestoreData.updatedAt || new Date().toISOString(),
      projectManager: firestoreData.projectManager || firestoreData.manager || 'Unassigned',
      estimatedCompletion: firestoreData.estimatedCompletion || firestoreData.completionDate,
      budget: firestoreData.budget || 0,
      area: Math.round(area),
      color: this.generateProjectColor(id),
      priority
    };
  }

  /**
   * Check if cache is valid
   */
  private isCacheValid(): boolean {
    return Date.now() - this.lastFetchTime < this.cacheExpiryTime;
  }

  /**
   * Fetch all polygon locations from Firestore
   */
  async getAllPolygonLocations(): Promise<ProjectLocation[]> {
    try {
      // Check cache
      if (this._allLocationsCache && this.isCacheValid()) {
        return this._allLocationsCache;
      }

      const polygonCollection = collection(this.db, 'polygon_locations');
      const q = query(polygonCollection, orderBy('updatedAt', 'desc'));
      const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(q);
      const locations: ProjectLocation[] = [];

      querySnapshot.forEach((doc: DocumentSnapshot<DocumentData>) => {
        const data = doc.data();
        if (data) {
          const projectLocation = this.convertFirestoreToProjectLocation(doc.id, data);
          locations.push(projectLocation);
          this.cache.set(doc.id, projectLocation);
        }
      });

      this.lastFetchTime = Date.now();
      this._allLocationsCache = locations;
      
      return locations;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Subscribe to real-time updates
   */
  subscribeToPolygonLocations(callback: ProjectLocationCallback): UnsubscribeFunction {
    const polygonCollection = collection(this.db, 'polygon_locations');
    const q = query(polygonCollection, orderBy('updatedAt', 'desc'));
    const listenerId = Math.random().toString(36).substr(2, 9);

    const unsubscribe: UnsubscribeFunction = onSnapshot(
      q,
      (querySnapshot: QuerySnapshot<DocumentData>) => {
        try {
          const locations: ProjectLocation[] = [];

          querySnapshot.forEach((doc: DocumentSnapshot<DocumentData>) => {
            const data = doc.data();
            if (data) {
              const projectLocation = this.convertFirestoreToProjectLocation(doc.id, data);
              locations.push(projectLocation);
              this.cache.set(doc.id, projectLocation);
            }
          });

          this.lastFetchTime = Date.now();
          this._allLocationsCache = locations;
          callback(locations);
        } catch {
          // Silent error handling
        }
      },
      () => {
        // Silent error handling
      }
    );

    this.listeners.set(listenerId, unsubscribe);
    return unsubscribe;
  }

  /**
   * Get a specific polygon location by ID
   */
  async getPolygonLocationById(id: string): Promise<ProjectLocation | null> {
    try {
      if (this.cache.has(id) && this.isCacheValid()) {
        return this.cache.get(id)!;
      }

      const docRef = doc(this.db, 'polygon_locations', id);
      const docSnap: DocumentSnapshot<DocumentData> = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const projectLocation = this.convertFirestoreToProjectLocation(id, data);
        this.cache.set(id, projectLocation);
        return projectLocation;
      }
      return null;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get filtered polygon locations
   */
  async getFilteredPolygonLocations(filter: ProjectLocationFilter): Promise<ProjectLocation[]> {
    const allLocations = await this.getAllPolygonLocations();
    
    return allLocations.filter(location => {
      if (filter.status && location.status !== filter.status) return false;
      if (filter.type && location.type !== filter.type) return false;
      if (filter.priority && location.priority !== filter.priority) return false;
      if (filter.minArea && location.area && location.area < filter.minArea) return false;
      if (filter.maxArea && location.area && location.area > filter.maxArea) return false;
      if (filter.updatedAfter) {
        const locationDate = new Date(location.updatedAt);
        if (locationDate < filter.updatedAfter) return false;
      }
      return true;
    });
  }

  /**
   * Get active polygon locations only
   */
  async getActivePolygonLocations(): Promise<ProjectLocation[]> {
    return this.getFilteredPolygonLocations({ status: 'active' });
  }

  /**
   * Get polygon locations by type
   */
  async getPolygonLocationsByType(type: 'construction' | 'renovation' | 'maintenance'): Promise<ProjectLocation[]> {
    return this.getFilteredPolygonLocations({ type });
  }

  /**
   * Get polygon locations by priority
   */
  async getPolygonLocationsByPriority(priority: 'low' | 'medium' | 'high' | 'critical'): Promise<ProjectLocation[]> {
    return this.getFilteredPolygonLocations({ priority });
  }

  /**
   * Search polygon locations
   */
  async searchPolygonLocations(searchTerm: string): Promise<ProjectLocation[]> {
    const allLocations = await this.getAllPolygonLocations();
    const searchLower = searchTerm.toLowerCase();
    
    return allLocations.filter(location => 
      location.name.toLowerCase().includes(searchLower) ||
      location.description.toLowerCase().includes(searchLower) ||
      (location.projectManager && location.projectManager.toLowerCase().includes(searchLower))
    );
  }

  /**
   * Get polygon locations statistics
   */
  async getPolygonLocationStats(): Promise<ProjectLocationStats> {
    const locations = await this.getAllPolygonLocations();
    
    return {
      total: locations.length,
      active: locations.filter(l => l.status === 'active').length,
      inactive: locations.filter(l => l.status === 'inactive').length,
      byType: locations.reduce((acc, location) => {
        acc[location.type] = (acc[location.type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      byStatus: locations.reduce((acc, location) => {
        acc[location.status] = (acc[location.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      byPriority: locations.reduce((acc, location) => {
        const priority = location.priority || 'medium';
        acc[priority] = (acc[priority] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      totalArea: locations.reduce((total, location) => total + (location.area || 0), 0),
      averagePolygonPoints: locations.length > 0 
        ? Math.round(locations.reduce((total, location) => total + location.polygon.length, 0) / locations.length)
        : 0,
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Get sorted polygon locations
   */
  async getSortedPolygonLocations(
    sortBy: 'name' | 'updatedAt' | 'area' | 'priority' | 'status',
    sortOrder: 'asc' | 'desc' = 'asc'
  ): Promise<ProjectLocation[]> {
    const locations = await this.getAllPolygonLocations();
    
    return locations.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'updatedAt':
          aValue = new Date(a.updatedAt).getTime();
          bValue = new Date(b.updatedAt).getTime();
          break;
        case 'area':
          aValue = a.area || 0;
          bValue = b.area || 0;
          break;
        case 'priority':
          const priorityOrder = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
          aValue = priorityOrder[a.priority || 'medium'];
          bValue = priorityOrder[b.priority || 'medium'];
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          aValue = a.name;
          bValue = b.name;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
    this._allLocationsCache = null;
    this.lastFetchTime = 0;
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; lastFetch: Date | null; isValid: boolean } {
    return {
      size: this.cache.size,
      lastFetch: this.lastFetchTime > 0 ? new Date(this.lastFetchTime) : null,
      isValid: this.isCacheValid()
    };
  }

  /**
   * Clean up all listeners
   */
  cleanup(): void {
    this.listeners.forEach((unsubscribe: UnsubscribeFunction) => unsubscribe());
    this.listeners.clear();
    this.clearCache();
  }
}

export const polygonLocationsService = new PolygonLocationsService();
export default polygonLocationsService;